import type { AtlasMeta } from '@/lib/schemas/atlas';

/**
 * 공간 아틀라스 노드 레지스트리 — 포지움 11장(궤도→지역→도시→거리→실내).
 * 계층: parentId가 행성이면 located_in, 장소면 parent_child(seedAtlas가 링크 생성). canon climb 위해 모든 장소 fields.parent=행성.
 * 핫스팟은 주요 하강 경로만(나머지는 브레드크럼/형제 폴백). 좌표는 근사(정밀은 loop 튜닝).
 */

export const ATLAS_PLANET = 'forgium';

export interface AtlasNodeSpec {
  nodeId: string;
  publicKey: string; // /atlas/forgium/<publicKey>.png
  parentId: string; // 상위 노드(행성=located_in, 장소=parent_child). 행성 노드는 ''.
  create?: { name: string; kind: string; desc: string }; // 신규 location 생성(기존 엔티티는 생략)
  meta: AtlasMeta;
}

export const ATLAS_NODES: AtlasNodeSpec[] = [
  {
    nodeId: 'forgium',
    publicKey: 'orbit',
    parentId: '',
    meta: {
      layer: 'orbit',
      night: true,
      vista: true,
      hotspots: [
        { id: 'hs-orbit-sigma', viewKey: 'orbit', x: 0.45, y: 0.55, kind: 'enter', to: 'loc-sigma', label: '시그마' },
        { id: 'hs-orbit-neon', viewKey: 'orbit', x: 0.3, y: 0.42, kind: 'enter', to: 'loc-neoncity', label: '네온시티' },
        { id: 'hs-orbit-inter', viewKey: 'orbit', x: 0.62, y: 0.5, kind: 'enter', to: 'loc-interchange', label: '위성도시' },
      ],
    },
  },
  {
    nodeId: 'loc-sigma',
    publicKey: 'sigma',
    parentId: ATLAS_PLANET,
    meta: {
      layer: 'city',
      night: false,
      hotspots: [
        { id: 'hs-sigma-plaza', viewKey: 'sigma', x: 0.5, y: 0.7, kind: 'enter', to: 'loc-sigma-plaza', label: '광장' },
        { id: 'hs-sigma-roof', viewKey: 'sigma', x: 0.78, y: 0.3, kind: 'enter', to: 'loc-sigma-rooftop', label: '전망대' },
      ],
    },
  },
  { nodeId: 'loc-sigma-rooftop', publicKey: 'sigma-rooftop', parentId: 'loc-sigma', create: { name: '시그마 전망대', kind: '옥상', desc: '시그마 마천루 옥상에서 내려다본 야경.' }, meta: { layer: 'street', night: true } },
  {
    nodeId: 'loc-sigma-plaza',
    publicKey: 'plaza',
    parentId: 'loc-sigma',
    create: { name: '시그마 광장', kind: '광장', desc: '시민과 콘티가 공존하는 중앙 광장 — 함께 만드는 더 나은 내일.' },
    meta: { layer: 'street', night: false, hotspots: [{ id: 'hs-plaza-quiver', viewKey: 'plaza', x: 0.82, y: 0.52, kind: 'enter', to: 'loc-quiver', label: '퀴버' }] },
  },
  { nodeId: 'loc-quiver', publicKey: 'quiver', parentId: 'loc-sigma-plaza', create: { name: '퀴버 라운지', kind: '라운지', desc: '오비탈 라운지 — 성운 천장 아래의 바.' }, meta: { layer: 'interior', night: true, indoor: true } },
  { nodeId: 'loc-neoncity', publicKey: 'neoncity', parentId: ATLAS_PLANET, meta: { layer: 'street', night: true } },
  {
    nodeId: 'loc-interchange',
    publicKey: 'interchange',
    parentId: ATLAS_PLANET,
    create: { name: '위성도시 교통결절', kind: '교통', desc: '모노레일과 비행로가 교차하는 결절.' },
    meta: { layer: 'region', night: false, hotspots: [{ id: 'hs-inter-factory', viewKey: 'interchange', x: 0.5, y: 0.62, kind: 'enter', to: 'loc-factory', label: '제조구역' }] },
  },
  { nodeId: 'loc-outskirts', publicKey: 'outskirts', parentId: ATLAS_PLANET, create: { name: '교외', kind: '외곽', desc: '흰 인프라와 폭포가 흐르는 도시 경계.' }, meta: { layer: 'region', night: false } },
  { nodeId: 'loc-frontier', publicKey: 'frontier', parentId: ATLAS_PLANET, create: { name: '외곽 경계', kind: '경계', desc: '도시와 사막이 만나는 변경 — 강과 풍력.' }, meta: { layer: 'region', night: false } },
  { nodeId: 'loc-habitat', publicKey: 'habitat', parentId: 'loc-sigma', create: { name: '거주구 A-17', kind: '거주', desc: '수직 정원의 주거 복합.' }, meta: { layer: 'interior', night: false, indoor: true } },
  { nodeId: 'loc-factory', publicKey: 'factory', parentId: 'loc-interchange', create: { name: '제조구역 07', kind: '산업', desc: '로봇 조립 라인의 생산 구역.' }, meta: { layer: 'interior', night: false, indoor: true } },
];

export const atlasPublicPath = (publicKey: string): string => `/atlas/forgium/${publicKey}.png`;

/** seedAtlas가 city 자산을 단독 소유하는 노드(seedCities 충돌 방지). */
export const ATLAS_OWNED_NODES = new Set(ATLAS_NODES.map((n) => n.nodeId));

/** nodeId → public 폴백 경로(IDB 자산 부재 시). */
export const ATLAS_NODE_FALLBACK: Record<string, string> = Object.fromEntries(ATLAS_NODES.map((n) => [n.nodeId, atlasPublicPath(n.publicKey)]));
